<template>
  <div>
    <div v-if="score && !hasEssentials">
      <GridHighlight
        type="stability"
        :text="score"
        :minScore="minScore"
        :maxScore="maxScore"
        :scoreDelta="scoreDelta"
        :large-text="true"
        class="ml-3"
        :show-title="true"
      ></GridHighlight>
    </div>
    <Loader v-if="!riskRatings" text="" color="white"></Loader>
    <div class="mt-2" v-if="riskRatings">
      <div class="text-left ml-3 mb-2 mr-2 pull-left">Risk Ratings</div>
      <PulseAndRiskDefinitions
        class="pull-left ratings-icon-container"
        :initial="'RR'"
      ></PulseAndRiskDefinitions>
      <div class="clearfix"></div>

      <RiskRatingPrimary
        :horizontal="true"
        :risks="riskRatings"
        :use-click="false"
        :dark="true"
        class="mb-5"
      ></RiskRatingPrimary>
    </div>
  </div>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";
import lodashRemove from "lodash/remove";
import lodashFind from "lodash/find";
import { getRiskRatingsByCountryCode } from "@/services/controlRisks/risks.service";
import { getStabilityDelta } from "@/services/grids/stability.service";
import lodashGet from "lodash/get";
import {
  hasEssentialsAccess,
  hasPlusCyberAccess,
  hasMaritimeAccess,
} from "@/services/user/user.service";
import { RISK_RATING_PRIMARIES } from "@/common/constants/controlRisks/risks";

export default {
  name: "AssetsPaneRatings",
  components: {
    PulseAndRiskDefinitions: () =>
      import("@/components/grids/PulseAndRiskDefinitions"),
    GridHighlight: () => import("@/components/grids/GridHighlight"),
    RiskRatingPrimary: () =>
      import("@/components/controlRisks/risks/RiskRatingPrimary"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    countryCode: {
      type: String,
    },
  },
  data() {
    const primaryRiskRatings = Object.keys(
      RISK_RATING_PRIMARIES
    ).map((key) => ({ key, value: RISK_RATING_PRIMARIES[key] }));
    if (!hasPlusCyberAccess()) {
      lodashRemove(primaryRiskRatings, (r) => {
        return r.key === "cyber";
      });
    }
    if (!hasMaritimeAccess()) {
      lodashRemove(primaryRiskRatings, (r) => {
        return r.value === RISK_RATING_PRIMARIES.maritime;
      });
    }
    return {
      primaryRiskRatings: primaryRiskRatings,
      hasEssentials: hasEssentialsAccess(),
      stability: null,
      riskRatingCountries: null,
    };
  },
  async mounted() {
    this.riskRatingCountries = await getRiskRatingsByCountryCode(
      this.countryCode
    );
  },
  computed: {
    riskRatingsCountry() {
      if (
        this.riskRatingCountries &&
        Array.isArray(this.riskRatingCountries.countries) &&
        this.countryCode
      ) {
        return (
          lodashFind(this.riskRatingCountries.countries, [
            "properties.iso2Code",
            this.countryCode.toUpperCase(),
          ]) || null
        );
      }
      return null;
    },
    riskRatings() {
      if (!this.riskRatingsCountry) {
        return null;
      }

      const primaryRiskCategoryIds = this.primaryRiskRatings.map((rr) => {
        return rr.value;
      });
      let ratings = [];
      this.riskRatingsCountry.properties.ratings.forEach((rating) => {
        if (
          lodashGet(rating, "riskCategoryId") &&
          primaryRiskCategoryIds.includes(rating.riskCategoryId)
        ) {
          this.$set(ratings, ratings.length, {
            id: rating.riskCategoryId,
            type: rating.riskCategoryName,
            value: rating.rateValue,
            order: this.primaryRiskRatings.findIndex(
              (x) => x.value == rating.riskCategoryId
            ),
          });
        }
      });
      return lodashOrderBy(ratings, ["order"], ["asc"]);
    },
    score() {
      let result = null;
      if (lodashGet(this.stability, "Item.count")) {
        result = this.stability.Item.count;
      }

      if (typeof result === "number") {
        // need to use Math.floor because the map color styling doesn't round at all so
        // 59.99 still will show yellow instead of green which is 60
        result = Math.floor(result);
      }
      return result;
    },
    minScore() {
      if (
        typeof lodashGet(this.stability, "Item.stats.percentiles.very_low") ===
        "number"
      ) {
        const min = this.stability.Item.stats.percentiles.very_low;
        return min < 1 ? min : Math.floor(min);
      }
      return null;
    },
    maxScore() {
      if (
        typeof lodashGet(this.stability, "Item.stats.percentiles.very_high") ===
        "number"
      ) {
        return Math.floor(this.stability.Item.stats.percentiles.very_high);
      }
      return null;
    },
    scoreDelta() {
      if (lodashGet(this.stability, "Item.scoreDelta")) {
        return Math.floor(this.stability.Item.scoreDelta);
      } else if (lodashGet(this.stability, `Item.delta["1"]`)) {
        return Math.floor(this.stability.Item.delta["1"]);
      }
      return 0;
    },
  },
  methods: {
    async getStability() {
      this.stability = null;
      if (this.countryCode) {
        const result = await getStabilityDelta({
          locationType: "country",
          numDays: 1,
        });
        if (result && result.pulseTiles) {
          for (let i = 0; i < result.pulseTiles.length; i++) {
            const country = result.pulseTiles[i];
            if (
              country.aoiId.toLowerCase() === this.countryCode.toLowerCase()
            ) {
              this.stability = {
                Item: country,
              };
              break;
            }
          }
        }
      }
    },
  },
  watch: {
    countryCode: {
      handler() {
        this.getStability();
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
